import { useCallback, useEffect, useRef } from 'react';

// Cross-origin iframes hide all internal activity from us. What we CAN observe:
// - `window.blur` firing while `document.activeElement === iframe` → user clicked into the iframe.
// - `window.focus`, `mousedown`, `keydown` on the parent → they're doing something in our page.
// - `mouseenter`/`mouseleave` on the iframe element → pointer crossed the iframe bounds.
// The timer starts on first iframe-focus and resets on any of the above signals.
// Anything happening purely inside the iframe is invisible — that's what the confirmation prompt is for.
export function useIframeInactivityTimeout({
  iframeEl,
  timeoutMs,
  onInactive
}: {
  iframeEl: HTMLIFrameElement | null;
  timeoutMs: number;
  onInactive: () => void;
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedRef = useRef(false);
  const startedAtRef = useRef<number | null>(null);
  const onInactiveRef = useRef(onInactive);
  onInactiveRef.current = onInactive;

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = null;
    startedRef.current = false;
    startedAtRef.current = null;
  }, []);

  const reset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onInactiveRef.current();
    }, timeoutMs);
    startedRef.current = true;
    startedAtRef.current = Date.now();
  }, [timeoutMs]);

  useEffect(() => {
    if (!iframeEl) {
      return;
    }

    const onWindowBlur = () => {
      const active = document.activeElement;
      const hitIframe = !!iframeEl && active === iframeEl;
      if (hitIframe) {
        reset();
      }
    };
    const onActivity = (_: Event) => {
      if (!startedRef.current) {
        return;
      }
      reset();
    };

    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('focus', onActivity);
    window.addEventListener('mousedown', onActivity);
    window.addEventListener('keydown', onActivity);
    iframeEl?.addEventListener('mouseenter', onActivity);
    iframeEl?.addEventListener('mouseleave', onActivity);

    return () => {
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onActivity);
      window.removeEventListener('mousedown', onActivity);
      window.removeEventListener('keydown', onActivity);
      iframeEl?.removeEventListener('mouseenter', onActivity);
      iframeEl?.removeEventListener('mouseleave', onActivity);
    };
  }, [iframeEl, reset]);

  useEffect(() => stop, [stop]);

  const resetExternal = useCallback(() => reset(), [reset]);

  return { reset: resetExternal, stop };
}
